import { useEffect, useRef } from 'react';
import { Outlet, useLoaderData, useNavigate } from 'react-router-dom';
import { useOktaAuth } from '@okta/okta-react';
import { toRelativeUrl } from '@okta/okta-auth-js';
import { useDispatch, useSelector } from 'react-redux';
import Box from '@mui/material/Box';
import * as Sentry from '@sentry/react';
import { Spinner } from '@app/components/Loader';
import SomethingWentWrong from '@app/components/SomethingWentWrong';
import ScrollToTop from '@app/components/ScrollToTop';
import SessionOverlays from '@app/components/SessionOverlays';
import { allowAuthFailurePageView } from '@app/constants/authFailureGate';
import { clearAuthFailureNavigation } from '@app/store/appState';

const AuthSpinner = () => (
  <Box display="flex" alignItems="center" justifyContent="center" height="100vh">
    <Spinner />
  </Box>
);

const AuthenticatedShell = () => {
  const loaderData = useLoaderData();
  const { oktaAuth, authState } = useOktaAuth();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const wasAuthenticatedRef = useRef(false);

  const shouldNavigateToAuthFailure = useSelector(
    state => state.appState.shouldNavigateToAuthFailure,
  );

  if (loaderData?.isAuthenticated || authState?.isAuthenticated) {
    wasAuthenticatedRef.current = true;
  }

  // Handle mid-session expiry: the loader only runs on navigation, so if the
  // token expires while the user is on a page, this re-initiates login.
  // TODO(auth-router): Transient `!isAuthenticated` while `!isPending` (e.g. token
  // renewal race) can also trigger a full Okta redirect; tighten conditions if needed.
  useEffect(() => {
    if (
      wasAuthenticatedRef.current &&
      authState &&
      !authState.isPending &&
      !authState.isAuthenticated
    ) {
      const originalUri = toRelativeUrl(
        window.location.href,
        window.location.origin,
      );
      oktaAuth.setOriginalUri(originalUri);
      oktaAuth.signInWithRedirect();
    }
  }, [oktaAuth, authState]);

  useEffect(() => {
    if (shouldNavigateToAuthFailure) {
      allowAuthFailurePageView();
      navigate('/auth-failure');
      dispatch(clearAuthFailureNavigation());
    }
  }, [shouldNavigateToAuthFailure, navigate, dispatch]);

  const showShell =
    loaderData?.isAuthenticated ||
    authState?.isAuthenticated ||
    (authState?.isPending && wasAuthenticatedRef.current);

  if (!showShell) {
    return <AuthSpinner />;
  }

  const onReset = () => {
    navigate('/');
    window.location.reload();
  };

  return (
    <Sentry.ErrorBoundary fallback={<SomethingWentWrong onClick={onReset} />}>
      <SessionOverlays />
      <ScrollToTop />
      <Outlet />
    </Sentry.ErrorBoundary>
  );
};

export default AuthenticatedShell;
